import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const skillGroups = [
  {
    category: "AI & DATA ENGINEERING",
    color: "wasabi",
    skills: [
      { name: "FastAPI / Flask", level: 90 },
      { name: "LangChain / RAG", level: 88 },
      { name: "SQL (PostgreSQL / CockroachDB)", level: 85 },
      { name: "pandas / numpy", level: 87 },
      { name: "ChromaDB / Vector DBs", level: 82 },
    ],
  },
  {
    category: "MOBILE & EDGE AI",
    color: "mint",
    skills: [
      { name: "Kotlin / Jetpack Compose", level: 86 },
      { name: "TensorFlow Lite (Edge ML)", level: 84 },
      { name: "Android SDK / Sensors", level: 88 },
      { name: "Room / Hilt / WorkManager", level: 83 },
      { name: "MVVM / Offline-First", level: 85 },
    ],
  },
  {
    category: "VIBE CODING (WEB & UI)",
    color: "wasabi",
    skills: [
      { name: "TypeScript / React", level: 86 },
      { name: "Next.js / Vite", level: 84 },
      { name: "Tailwind CSS", level: 92 },
      { name: "Three.js / GSAP", level: 78 },
      { name: "WebSockets", level: 80 },
    ],
  },
  {
    category: "VIBE CODING (CORE TOOLS)",
    color: "mint",
    skills: [
      { name: "Python", level: 94 },
      { name: "Kotlin", level: 86 },
      { name: "TypeScript", level: 84 },
      { name: "SQL", level: 85 },
      { name: "Git / CI-CD", level: 82 },
    ],
  },
];

const timeline = [
  {
    year: "2024",
    title: "B.E. AI & Data Engineering",
    org: "CSE AIDE Department · Bengaluru",
    desc: "Started the journey into AI and Data Engineering. Built foundational skills in neural networks, data structures, and Python backend development.",
    tags: ["Python", "TensorFlow", "SQL"],
  },
  {
    year: "2025",
    title: "Backend Lead — InsightDesk AI",
    org: "Vanguard Neural Systems · Team Project",
    desc: "Led backend architecture for a multi-modal AI quality orchestration platform. Designed RAG pipelines with LangChain and ChromaDB for real-time voice evaluation.",
    tags: ["FastAPI", "LangChain", "RAG"],
  },
  {
    year: "2025",
    title: "Edge AI — Healer & CarbonTrack",
    org: "Personal Projects · Android",
    desc: "Built two production-grade Android apps: an on-device accident detection system (Healer) fusing 9+ sensors with TFLite, and a privacy-first carbon tracking gamification app.",
    tags: ["Kotlin", "TFLite", "Android"],
  },
  {
    year: "2025",
    title: "Agentic AI — Jarvis & Aegis",
    org: "Personal + Collaborative Projects",
    desc: "Created a voice-first desktop AI assistant with full-duplex WebSocket orchestration, and a cognitive scam-defense platform with multimodal threat detection pipelines.",
    tags: ["NVIDIA NIM", "WebSockets", "Gemini"],
  },
  {
    year: "NOW",
    title: "Founder · Axiom Digital Studio",
    org: "Studio Lead · Bengaluru",
    desc: "Leading the digital presence arm — shipping high-conversion web ecosystems for brands. Building the 7 Beast Projects portfolio and scaling the studio internationally.",
    tags: ["Next.js", "Three.js", "GSAP"],
    active: true,
  },
];

const SkillBar = ({ name, level, color }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const isWasabi = color === "wasabi";

  return (
    <div ref={ref} className="mb-3">
      <div className="flex justify-between mb-1.5">
        <span className="font-mono text-xs text-foreground">{name}</span>
        <span className={`font-mono text-xs ${isWasabi ? "text-primary" : "text-accent"}`}>
          {level}%
        </span>
      </div>
      <div className="h-1 rounded-full bg-muted overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${isWasabi ? "bg-primary" : "bg-accent"}`}
          initial={{ width: 0 }}
          animate={isInView ? { width: `${level}%` } : { width: 0 }}
          transition={{ duration: 1.1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </div>
  );
};

export const RoadmapAndSkills = () => {
  const [activeTab, setActiveTab] = useState("skills");

  return (
    <section id="skills" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <div className="eyebrow mb-3">// ROADMAP_AND_SKILLS</div>
          <div className="flex items-end gap-6">
            <h2 className="font-display font-bold text-4xl sm:text-5xl text-foreground">
              The{" "}
              <span className="text-gradient-wasabi-mint">Engineering Stack</span>
            </h2>
            <div className="hidden sm:block flex-1 h-px bg-border mb-2" />
          </div>
        </motion.div>

        {/* Tab switcher */}
        <div className="flex gap-1 p-1 glass-card rounded-xl w-fit mb-10">
          {["skills", "timeline"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2 rounded-lg font-mono text-xs tracking-widest uppercase transition-all duration-200 ${
                activeTab === tab
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab === "skills" ? "SKILLS_MAP" : "CAREER_LOG"}
            </button>
          ))}
        </div>

        {/* SKILLS TAB */}
        {activeTab === "skills" && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {skillGroups.map((group, gi) => (
              <motion.div
                key={group.category}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.55, delay: gi * 0.08 }}
                className="glass-card rounded-2xl p-6"
              >
                <div
                  className={`eyebrow mb-5 ${
                    group.color === "wasabi" ? "text-primary" : "text-accent"
                  }`}
                >
                  {group.category}
                </div>
                {group.skills.map((skill) => (
                  <SkillBar
                    key={skill.name}
                    name={skill.name}
                    level={skill.level}
                    color={group.color}
                  />
                ))}
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* TIMELINE TAB */}
        {activeTab === "timeline" && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="relative max-w-3xl"
          >
            {/* Vertical line */}
            <div className="absolute left-[76px] top-0 bottom-0 w-px bg-border" />

            <div className="space-y-8">
              {timeline.map((item, i) => (
                <motion.div
                  key={item.year + item.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex gap-8"
                >
                  {/* Year */}
                  <div className="flex flex-col items-end pt-1 w-16 flex-shrink-0">
                    <span
                      className={`font-mono text-xs font-semibold ${
                        item.active ? "text-primary" : "text-muted-foreground"
                      }`}
                    >
                      {item.year}
                    </span>
                  </div>

                  {/* Dot on timeline */}
                  <div className="relative flex-shrink-0 mt-1.5">
                    <div
                      className={`w-2.5 h-2.5 rounded-full z-10 relative ${
                        item.active ? "bg-primary pulse-dot" : "bg-border border-2 border-muted"
                      }`}
                    />
                  </div>

                  {/* Content */}
                  <div
                    className={`flex-1 glass-card rounded-xl p-5 ${
                      item.active ? "border-primary/30" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="font-display font-semibold text-base text-foreground">
                        {item.title}
                      </h3>
                      {item.active && (
                        <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-primary/15 border border-primary/30 font-mono text-xs text-primary">
                          CURRENT
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-xs text-accent mb-3">{item.org}</div>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                      {item.desc}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded text-xs font-mono border border-border text-muted-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default RoadmapAndSkills;
